import { useTranslation } from 'react-i18next';
import { CheckCircle2, History as HistoryIcon, TriangleAlert } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { LoadingState } from '@/components/ui/states';
import type { ForecastHealth } from './lib';

/**
 * "Is forecasting working" — not "what does it forecast".
 *
 * ADR-0031 §2.6: `hasTradingHistory: false` is a quiet, healthy state (a
 * fresh tenant with nothing to forecast from yet) and has to read differently
 * from a sweep that read zero rows on a branch that DOES have trading
 * history — the unbound-tenant-read signature under `NOBYPASSRLS`. Both are
 * "no forecast", so this banner is what tells them apart before the chart
 * below ever has to.
 *
 * Presentational: `ForecastingTab` fetches `/forecasting/health` once and
 * hands the result down here AND to the forecast panel below, which needs
 * `hasTradingHistory` to word its own empty state honestly.
 */
export default function HealthBanner({
  data,
  loading,
  error,
  reload,
}: {
  data: ForecastHealth | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}) {
  const { t, i18n } = useTranslation();

  if (loading && !data) return <LoadingState label={t('common.loading')} />;
  if (!data) {
    if (error) {
      return (
        <Card className="border-red-300 dark:border-red-900">
          <CardContent className="flex items-center gap-3 p-4 text-sm">
            <Icon as={TriangleAlert} className="shrink-0 text-red-600 dark:text-red-400" />
            <span>{t('forecasting.health.loadFailed')}</span>
            <button onClick={reload} className="ms-auto text-accent underline">
              {t('common.tryAgain')}
            </button>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  // A brand-new branch: no trading history at all. This is a real, distinct
  // state from "healthy" (a normal running sweep) and from "broken" (a
  // problem needing attention) — see ADR-0031 §2.6.
  if (!data.hasTradingHistory && data.healthy) {
    return (
      <Card className="border-sky-300 dark:border-sky-900">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Icon as={HistoryIcon} size="lg" className="shrink-0 text-sky-600 dark:text-sky-400" />
          <div>
            <p className="text-sm font-semibold">{t('forecasting.health.noHistory')}</p>
            <p className="text-sm text-ink-muted">{t('forecasting.health.noHistoryHint')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={data.healthy ? undefined : 'border-red-300 dark:border-red-900'}>
      <CardContent className="flex flex-wrap items-center gap-3 p-4">
        <Icon
          as={data.healthy ? CheckCircle2 : TriangleAlert}
          size="lg"
          className={
            data.healthy
              ? 'shrink-0 text-green-600 dark:text-green-400'
              : 'shrink-0 text-red-600 dark:text-red-400'
          }
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {t(data.healthy ? 'forecasting.health.healthy' : 'forecasting.health.broken')}
          </p>
          {data.problems.length > 0 ? (
            <ul className="mt-1 space-y-1">
              {data.problems.map((problem, i) => (
                <li key={i} className="text-sm text-ink-muted">
                  {problem}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-muted">{t('forecasting.health.healthyHint')}</p>
          )}
        </div>
        {data.pendingSuggestions > 0 && (
          <Badge tone="info">
            {t('forecasting.health.pendingSuggestions', { count: data.pendingSuggestions })}
          </Badge>
        )}
        <span className="w-full text-xs text-ink-muted">
          {t('forecasting.health.lastSuccessfulRunAt')}{' '}
          {data.lastSuccessfulRunAt ? formatDateTime(data.lastSuccessfulRunAt, i18n.language) : '—'}
        </span>
      </CardContent>
    </Card>
  );
}
